import { Controller, useFormContext } from 'react-hook-form';

import {
  AlphanumericRegex,
  AlphanumericValidationType,
  EntityType,
} from '@polyai/stream/store/reducers/entities/types';
import Toggle from '@polyai/ui/components/atoms/Toggle';
import Tooltip from '@polyai/ui/components/atoms/Tooltip';
import type { OptionsType } from '@polyai/ui/components/molecules/Filter/Filter.constants';

import { FormSelect, FormTextInput } from 'components/molecules/Form';
import * as Styled from '../EntitySidebar.styled';
import { ENTITY_SIDEBAR_UI_COPY } from '../EntitySidebar.uiCopy';
import type { EntityFormProps, EntitySchemaProps } from '.';
import { ALPHANUMERIC_VALIDATION_OPTIONS } from './constants';

interface Props {
  readonly: boolean;
}

export const AlphanumericForm: React.FC<Props> = ({ readonly }) => {
  const { control, watch, setValue } = useFormContext<EntitySchemaProps>();

  const config = watch('config');

  const isAlphanumericConfig = config?.$case === EntityType.ALPHANUMERIC;
  const isEnabled = isAlphanumericConfig && config?.value.enabled;
  const validationType = isAlphanumericConfig
    ? config?.value.validationType
    : undefined;

  const handleValidationTypeChange = (e: OptionsType) => {
    setValue('config.value.validationType', e.value, {
      shouldDirty: true,
    });
    if (e.value === AlphanumericValidationType.ZIP_CODE) {
      setValue(
        'config.value.regularExpression',
        AlphanumericRegex.US_ZIP_CODE,
        { shouldDirty: true },
      );
    } else if (e.value === AlphanumericValidationType.POSTAL_CODE) {
      setValue(
        'config.value.regularExpression',
        AlphanumericRegex.UK_POSTAL_CODE,
        { shouldDirty: true },
      );
    } else {
      setValue('config.value.regularExpression', '', { shouldDirty: true });
    }
  };

  return (
    <>
      <Controller
        control={control}
        name="config.value.enabled"
        render={({ field }) => (
          <Styled.SwitchWrapper>
            <Toggle
              handleToggle={field.onChange}
              readonly={readonly}
              toggleOn={!!field.value}
            />
            <Tooltip
              content={ENTITY_SIDEBAR_UI_COPY.form.alphanumeric.toggle.helpText}
            >
              <Styled.ToggleText>Set validation type</Styled.ToggleText>
            </Tooltip>
          </Styled.SwitchWrapper>
        )}
      />

      {isEnabled && (
        <FormSelect<EntityFormProps>
          controlKey={'config.value.validationType' as any}
          data-test-id="validation-type-select"
          label="Validation type"
          options={ALPHANUMERIC_VALIDATION_OPTIONS}
          readonly={readonly}
          value={ALPHANUMERIC_VALIDATION_OPTIONS.find(
            option => option.value === validationType,
          )}
          onChange={handleValidationTypeChange}
        />
      )}

      {isEnabled && validationType === AlphanumericValidationType.CUSTOM && (
        <FormTextInput<EntityFormProps>
          controlKey={'config.value.regularExpression' as any}
          data-test-id="entity-regular-expression"
          help="Enter a regular expression to validate the entity"
          label="Expression"
          readOnly={readonly}
        />
      )}
    </>
  );
};
